import { Chess } from "@vendor/chess.js";
import { captureGain, moveSafety } from "../chess/exchange.ts";
import type { ChessMove } from "../chess/chessTypes.ts";
import { attackMap, computeCenterControl } from "./boardOverlays.ts";

/**
 * What the safety and structure board guides show, worked out from the
 * position alone. Each guide returns facts; guideShapes turns the active
 * guides into shapes for the board and guideNotes into a few plain sentences
 * (the same facts in words, for anyone who cannot or would rather not read
 * colours).
 */

type Side = "w" | "b";
type Square = Parameters<Chess["get"]>[0];

const NAMES: Record<string, string> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
};
const VALUES: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 100 };
const other = (side: Side): Side => (side === "w" ? "b" : "w");

function load(fen: string): Chess | null {
  try {
    return new Chess(fen);
  } catch {
    return null;
  }
}

function piecesOf(chess: Chess, side?: Side): Array<{ square: string; type: string; color: Side }> {
  const out: Array<{ square: string; type: string; color: Side }> = [];
  for (const file of "abcdefgh")
    for (const rank of "12345678") {
      const square = `${file}${rank}`;
      const piece = chess.get(square as Square);
      if (piece && (!side || piece.color === side))
        out.push({ square, type: piece.type, color: piece.color as Side });
    }
  return out;
}

/** The same position with `toMove` to move and no en passant square. */
function withTurn(fen: string, toMove: Side): Chess | null {
  const parts = fen.split(" ");
  if (parts.length < 6) return null;
  parts[1] = toMove;
  parts[3] = "-";
  return load(parts.join(" "));
}

// ---------------------------------------------------------------------------
// Threatened pieces

export type ThreatLevel = "check" | "loses" | "attacked";
export type Threat = {
  square: string;
  piece: string;
  level: ThreatLevel;
  attackers: number;
  defenders: number;
};

/**
 * The side's pieces the opponent attacks, as if it were the opponent's move.
 * "loses": a capture on that square wins material once the exchange is
 * played out (an undefended piece, or one attacked by a cheaper piece);
 * "attacked": it is attacked but holds; "check": the king is in check.
 */
export function threatLevels(fen: string, side: Side): Threat[] {
  const chess = load(fen);
  if (!chess) return [];
  const threats: Threat[] = [];
  const attacks = attackMap(fen);
  if (chess.turn() === side && chess.inCheck()) {
    const king = piecesOf(chess, side).find((p) => p.type === "k");
    if (king)
      threats.push({
        square: king.square,
        piece: "k",
        level: "check",
        attackers: (attacks[other(side)][king.square] || []).length,
        defenders: 0,
      });
  }
  const probe = chess.turn() === side ? withTurn(fen, other(side)) : chess;
  if (!probe) return threats;
  const best = new Map<string, number>();
  for (const move of probe.moves({ verbose: true }) as ChessMove[]) {
    if (!move.captured || !String(move.flags || "").match(/[ce]/)) continue;
    const square = String(move.flags).includes("e") ? `${move.to[0]}${move.from[1]}` : move.to;
    const gain = captureGain(probe, move);
    best.set(square, Math.max(best.get(square) ?? -Infinity, gain));
  }
  for (const [square, gain] of best) {
    const piece = chess.get(square as Square);
    if (!piece || piece.color !== side) continue;
    threats.push({
      square,
      piece: piece.type,
      level: gain > 0 ? "loses" : "attacked",
      attackers: (attacks[other(side)][square] || []).length,
      defenders: (attacks[side][square] || []).length,
    });
  }
  return threats.sort((a, b) => VALUES[b.piece] - VALUES[a.piece]);
}

// ---------------------------------------------------------------------------
// Loose pieces

export type LoosePiece = { square: string; piece: string; color: Side };

const START: Record<string, string> = {
  a1: "wr",
  b1: "wn",
  c1: "wb",
  d1: "wq",
  f1: "wb",
  g1: "wn",
  h1: "wr",
  a8: "br",
  b8: "bn",
  c8: "bb",
  d8: "bq",
  f8: "bb",
  g8: "bn",
  h8: "br",
};

/**
 * Knights, bishops, rooks and queens of either side that nothing defends,
 * once they have left their starting squares (a rook in its corner before
 * castling is loose by nature, and saying so on every move is noise).
 */
export function loosePieces(fen: string): LoosePiece[] {
  const chess = load(fen);
  if (!chess) return [];
  const attacks = attackMap(fen);
  return piecesOf(chess)
    .filter((p) => p.type !== "k" && p.type !== "p")
    .filter((p) => START[p.square] !== `${p.color}${p.type}`)
    .filter((p) => !(attacks[p.color][p.square] || []).length)
    .map((p) => ({ square: p.square, piece: p.type, color: p.color }));
}

// ---------------------------------------------------------------------------
// Pins

export type Pin = {
  pinned: string;
  pinnedPiece: string;
  pinner: string;
  pinnerPiece: string;
  target: string;
  targetPiece: string;
  /** The pinned piece's side. */
  color: Side;
  /** Pinned to the king: it may not move at all. */
  absolute: boolean;
};

const LINES: Record<string, number[][]> = {
  b: [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ],
  r: [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ],
};

/**
 * Pieces of either side pinned by a bishop, rook or queen: to their king
 * (absolute), or to a queen or rook worth more than the pinned piece.
 */
export function pins(fen: string): Pin[] {
  const chess = load(fen);
  if (!chess) return [];
  const found: Pin[] = [];
  const at = (f: number, r: number) =>
    f >= 0 && f < 8 && r >= 0 && r < 8
      ? chess.get(`${"abcdefgh"[f]}${r + 1}` as Square)
      : undefined;
  for (const slider of piecesOf(chess).filter((p) => "brq".includes(p.type))) {
    const directions = slider.type === "q" ? [...LINES.b, ...LINES.r] : LINES[slider.type];
    const f0 = slider.square.charCodeAt(0) - 97;
    const r0 = Number(slider.square[1]) - 1;
    for (const [df, dr] of directions) {
      let f = f0 + df;
      let r = r0 + dr;
      let first: { square: string; type: string; color: string } | null = null;
      while (f >= 0 && f < 8 && r >= 0 && r < 8) {
        const piece = at(f, r);
        const square = `${"abcdefgh"[f]}${r + 1}`;
        if (piece) {
          if (!first) {
            if (piece.color === slider.color) break;
            first = { square, type: piece.type, color: piece.color };
          } else {
            if (piece.color === first.color) {
              const absolute = piece.type === "k";
              // To a queen or rook, it is a pin only if what stands behind is worth more.
              const relative = "qr".includes(piece.type) && VALUES[piece.type] > VALUES[first.type];
              if (absolute || relative)
                found.push({
                  pinned: first.square,
                  pinnedPiece: first.type,
                  pinner: slider.square,
                  pinnerPiece: slider.type,
                  target: square,
                  targetPiece: piece.type,
                  color: first.color as Side,
                  absolute,
                });
            }
            break;
          }
        }
        f += df;
        r += dr;
      }
    }
  }
  return found;
}

// ---------------------------------------------------------------------------
// Pawn structure

export type PawnFlag = "isolated" | "doubled" | "passed";
export type PawnNote = { square: string; color: Side; flags: PawnFlag[] };

/** Isolated, doubled and passed pawns of both sides. */
export function pawnStructure(fen: string): PawnNote[] {
  const chess = load(fen);
  if (!chess) return [];
  const pawns = piecesOf(chess).filter((p) => p.type === "p");
  const notes: PawnNote[] = [];
  for (const pawn of pawns) {
    const file = pawn.square.charCodeAt(0) - 97;
    const rank = Number(pawn.square[1]);
    const own = pawns.filter((p) => p.color === pawn.color);
    const theirs = pawns.filter((p) => p.color !== pawn.color);
    const fileOf = (p: { square: string }) => p.square.charCodeAt(0) - 97;
    const flags: PawnFlag[] = [];
    if (!own.some((p) => Math.abs(fileOf(p) - file) === 1)) flags.push("isolated");
    if (own.some((p) => p !== pawn && fileOf(p) === file)) flags.push("doubled");
    const ahead = (p: { square: string }) =>
      pawn.color === "w" ? Number(p.square[1]) > rank : Number(p.square[1]) < rank;
    if (!theirs.some((p) => Math.abs(fileOf(p) - file) <= 1 && ahead(p))) flags.push("passed");
    if (flags.length) notes.push({ square: pawn.square, color: pawn.color, flags });
  }
  return notes;
}

// ---------------------------------------------------------------------------
// The opponent's last move

export type MoveIdea = {
  from: string;
  to: string;
  san: string;
  check: boolean;
  /** The side's pieces the move newly attacks, and the piece attacking each. */
  targets: Array<{ attacker: string; square: string; piece: string; loses: boolean }>;
};

/**
 * What the opponent's last move does to the side: the pieces it newly
 * attacks, including attacks it uncovers from a piece behind it.
 */
export function lastMoveIdea(
  fenBefore: string,
  move: { from: string; to: string; san: string },
  side: Side
): MoveIdea | null {
  const before = load(fenBefore);
  if (!before) return null;
  const mover = before.get(move.from as Square);
  if (!mover || mover.color === side) return null;
  let played: ChessMove | null = null;
  try {
    played = before.move(move.san) as ChessMove;
  } catch {
    return null;
  }
  if (!played) return null;
  const after = before.fen();
  const was = attackMap(fenBefore)[other(side)];
  const now = attackMap(after)[other(side)];
  const losing = new Set(
    threatLevels(after, side)
      .filter((t) => t.level !== "attacked")
      .map((t) => t.square)
  );
  const targets: MoveIdea["targets"] = [];
  const chess = load(after)!;
  for (const piece of piecesOf(chess, side)) {
    if (piece.type === "k") continue;
    const old = new Set((was[piece.square] || []).map((a) => a.square));
    const fresh = (now[piece.square] || []).find(
      (a) => !old.has(a.square) && !(a.square === move.to && old.has(move.from))
    );
    if (fresh)
      targets.push({
        attacker: fresh.square,
        square: piece.square,
        piece: piece.type,
        loses: losing.has(piece.square),
      });
  }
  return {
    from: move.from,
    to: move.to,
    san: move.san,
    check: chess.inCheck(),
    targets: targets.sort(
      (a, b) => Number(b.loses) - Number(a.loses) || VALUES[b.piece] - VALUES[a.piece]
    ),
  };
}

// ---------------------------------------------------------------------------
// Safe squares for a piece the student picks up

export type SquareVerdict = "wins" | "safe" | "loses" | "mate";
export type DestinationVerdict = {
  to: string;
  verdict: SquareVerdict;
  net: number;
  capture: boolean;
};

/**
 * For each legal destination of the piece on `from`: what the move does to
 * the material balance once the trades are counted, across the whole board.
 * "wins": it comes out ahead; "safe": nothing is lost; "loses": the opponent
 * then wins material (the moved piece, or something the move leaves or
 * already left hanging); "mate": it allows mate in one. A count of trades,
 * not an engine: quiet tactics beyond the exchanges are not seen.
 */
export function safeSquares(fen: string, from: string): DestinationVerdict[] {
  const chess = load(fen);
  if (!chess) return [];
  const piece = chess.get(from as Square);
  if (!piece || piece.color !== chess.turn()) return [];
  const moves = chess.moves({ verbose: true, square: from as Square }) as ChessMove[];
  const safety = moveSafety(chess, moves);
  const best = new Map<string, DestinationVerdict>();
  for (const move of moves) {
    const result = safety.get(move);
    if (!result) continue;
    const verdict: SquareVerdict = result.allowsMate
      ? "mate"
      : result.net > 0
        ? "wins"
        : result.net < 0
          ? "loses"
          : "safe";
    const entry = {
      to: move.to,
      verdict,
      net: result.allowsMate ? -100 : result.net,
      capture: Boolean(move.captured),
    };
    // Promotions reach one square several ways; keep the best.
    const previous = best.get(move.to);
    if (!previous || entry.net > previous.net) best.set(move.to, entry);
  }
  return [...best.values()];
}

/** The verdicts in words, for the announcement when a piece is selected. */
export function safeSquaresSentence(fen: string, from: string): string {
  const verdicts = safeSquares(fen, from);
  if (!verdicts.length) return "";
  const of = (kind: SquareVerdict) => verdicts.filter((v) => v.verdict === kind).map((v) => v.to);
  const parts = [
    of("wins").length ? `wins material on ${list(of("wins"))}` : "",
    of("safe").length ? `safe on ${list(of("safe"))}` : "",
    of("loses").length ? `loses material on ${list(of("loses"))}` : "",
    of("mate").length ? `allows mate after ${list(of("mate"))}` : "",
  ].filter(Boolean);
  return `${parts.join("; ")}.`.replace(/^./, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Center control in the student's terms

export function centerVerdict(
  net: number,
  side: Side
): { text: string; tone: "you" | "them" | "even" } {
  const mine = side === "w" ? net : -net;
  if (mine >= 8) return { text: "You dominate the center.", tone: "you" };
  if (mine >= 4) return { text: "You hold the center.", tone: "you" };
  if (mine >= 2) return { text: "You have the edge in the center.", tone: "you" };
  if (mine <= -8) return { text: "Your opponent dominates the center.", tone: "them" };
  if (mine <= -4) return { text: "Your opponent holds the center.", tone: "them" };
  if (mine <= -2) return { text: "Your opponent has the edge in the center.", tone: "them" };
  return { text: "The center is evenly contested.", tone: "even" };
}

/** How the last move changed the center, from the side's point of view. */
export function centerChange(fenBefore: string, fenAfter: string, side: Side): number {
  const sign = side === "w" ? 1 : -1;
  return sign * (computeCenterControl(fenAfter).net - computeCenterControl(fenBefore).net);
}

// ---------------------------------------------------------------------------
// Shapes and notes for the active guides

export type GuideKey = "threatened" | "loose" | "lastMove" | "pins" | "pawns" | "reply";

export type GuideTone = "danger" | "caution" | "target" | "info" | "mine" | "theirs";

export type GuideShape =
  | { kind: "fill"; square: string; tone: GuideTone; strength?: number }
  | { kind: "ring"; square: string; tone: GuideTone; dashed?: boolean }
  | { kind: "badge"; square: string; text: string; tone: GuideTone }
  | { kind: "arrow"; from: string; to: string; tone: GuideTone }
  | { kind: "line"; from: string; to: string; tone: GuideTone };

export type GuideInput = {
  fen: string;
  side: Side;
  /** The last move played, with the position before it. */
  last?: { fenBefore: string; from: string; to: string; san: string } | null;
  /** The squares the reply card names, while the student asks to see them. */
  replySquares?: string[] | null;
};

const BADGE: Record<PawnFlag, string> = { isolated: "I", doubled: "D", passed: "P" };

export function guideShapes(
  active: Partial<Record<GuideKey, boolean>>,
  input: GuideInput
): GuideShape[] {
  const shapes: GuideShape[] = [];
  const { fen, side } = input;
  if (active.pawns) {
    for (const note of pawnStructure(fen))
      shapes.push({
        kind: "badge",
        square: note.square,
        text: note.flags.map((flag) => BADGE[flag]).join(""),
        tone: note.color === side ? "mine" : "theirs",
      });
  }
  if (active.threatened) {
    for (const threat of threatLevels(fen, side)) {
      if (threat.level === "attacked") {
        shapes.push({ kind: "ring", square: threat.square, tone: "caution" });
        continue;
      }
      // The fill alone hides under a piece set that covers its square, so a
      // piece you would lose also gets a solid ring drawn over it.
      shapes.push({
        kind: "fill",
        square: threat.square,
        tone: "danger",
        strength: threat.level === "check" ? 2 : 1,
      });
      shapes.push({ kind: "ring", square: threat.square, tone: "danger" });
    }
  }
  if (active.loose) {
    for (const piece of loosePieces(fen))
      shapes.push({
        kind: "ring",
        square: piece.square,
        tone: piece.color === side ? "caution" : "target",
        dashed: true,
      });
  }
  if (active.pins) {
    for (const pin of pins(fen)) {
      const tone: GuideTone = pin.color === side ? "danger" : "target";
      shapes.push({ kind: "line", from: pin.pinner, to: pin.target, tone });
      shapes.push({ kind: "ring", square: pin.pinned, tone });
    }
  }
  if (active.lastMove && input.last) {
    const idea = lastMoveIdea(input.last.fenBefore, input.last, side);
    if (idea) {
      shapes.push({ kind: "arrow", from: idea.from, to: idea.to, tone: "info" });
      for (const target of idea.targets)
        shapes.push({
          kind: "arrow",
          from: target.attacker,
          to: target.square,
          tone: target.loses ? "danger" : "caution",
        });
    }
  }
  if (active.reply && input.replySquares?.length) {
    // The card's prose, on the board: a dashed ring on each square it names.
    for (const square of input.replySquares)
      shapes.push({ kind: "ring", square, tone: "info", dashed: true });
  }
  return shapes;
}

const named = (piece: string, square: string) => `${NAMES[piece]} on ${square}`;
function list(items: string[]): string {
  if (items.length <= 1) return items[0] || "";
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

/** The active guides' findings in a few plain sentences. */
export function guideNotes(
  active: Partial<Record<GuideKey, boolean>>,
  input: GuideInput
): string[] {
  const notes: string[] = [];
  const { fen, side } = input;
  if (active.lastMove && input.last) {
    const idea = lastMoveIdea(input.last.fenBefore, input.last, side);
    if (idea) {
      const hits = idea.targets.map((t) => named(t.piece, t.square));
      notes.push(
        idea.check
          ? `Their last move, ${idea.san}, gives check.`
          : hits.length
            ? `Their last move, ${idea.san}, attacks your ${list(hits)}.`
            : `Their last move, ${idea.san}, attacks none of your pieces directly.`
      );
    }
  }
  if (active.threatened) {
    const threats = threatLevels(fen, side);
    const check = threats.find((t) => t.level === "check");
    const loses = threats.filter((t) => t.level === "loses").map((t) => named(t.piece, t.square));
    const holds = threats
      .filter((t) => t.level === "attacked")
      .map((t) => named(t.piece, t.square));
    if (check) notes.push("Your king is in check.");
    if (loses.length) notes.push(`You would lose material on: your ${list(loses)}.`);
    if (holds.length) notes.push(`Attacked but defended enough: your ${list(holds)}.`);
  }
  if (active.loose) {
    const loose = loosePieces(fen);
    const mine = loose.filter((p) => p.color === side).map((p) => named(p.piece, p.square));
    const theirs = loose.filter((p) => p.color !== side).map((p) => named(p.piece, p.square));
    if (mine.length) notes.push(`Undefended: your ${list(mine)}.`);
    if (theirs.length) notes.push(`Undefended: their ${list(theirs)}.`);
  }
  if (active.pins) {
    const found = pins(fen);
    for (const pin of found) {
      const whose = pin.color === side ? "Your" : "Their";
      notes.push(
        `${whose} ${named(pin.pinnedPiece, pin.pinned)} is pinned to the ${NAMES[pin.targetPiece]} by the ${named(pin.pinnerPiece, pin.pinner)}${pin.absolute ? ": it cannot move" : ""}.`
      );
    }
  }
  if (active.pawns) {
    const structure = pawnStructure(fen);
    const describe = (color: Side, flag: PawnFlag) =>
      structure.filter((n) => n.color === color && n.flags.includes(flag)).map((n) => n.square);
    for (const [color, whose] of [
      [side, "Your"],
      [other(side), "Their"],
    ] as const) {
      const parts = (["isolated", "doubled", "passed"] as PawnFlag[])
        .map((flag) => {
          const squares = describe(color, flag);
          return squares.length ? `${flag} ${list(squares)}` : "";
        })
        .filter(Boolean);
      if (parts.length) notes.push(`${whose} pawns: ${parts.join("; ")}.`);
    }
  }
  return notes;
}
